/**
 * useSegmentPlayback - Playback state management for segment editor
 */
import { useState, useCallback, useRef } from 'react';
import { getEffectiveDuration } from '../utils/segmentUtils';

export function useSegmentPlayback(segments, totalDuration) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const positionRef = useRef(0);

  // Find segment at a given position
  const getSegmentAtPosition = useCallback((positionMs) => {
    if (!segments?.length) return null;
    return segments.find(segment => {
      const effectiveStart = segment.start_ms || 0;
      const effectiveDuration = getEffectiveDuration(segment);
      return positionMs >= effectiveStart && positionMs < effectiveStart + effectiveDuration;
    });
  }, [segments]);

  // Get segment index at position
  const getSegmentIndexAtPosition = useCallback((positionMs) => {
    if (!segments?.length) return -1;
    return segments.findIndex(segment => {
      const effectiveStart = segment.start_ms || 0;
      const effectiveDuration = getEffectiveDuration(segment);
      return positionMs >= effectiveStart && positionMs < effectiveStart + effectiveDuration;
    });
  }, [segments]);

  // Get current position
  const getPosition = useCallback(() => positionRef.current, []);

  // Play
  const play = useCallback(() => {
    if (positionRef.current >= totalDuration) {
      positionRef.current = 0;
      setPlayheadPosition(0);
    }
    setIsPlaying(true);
  }, [totalDuration]);

  // Pause
  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Toggle
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Seek
  const seek = useCallback((positionMs) => {
    const clamped = Math.max(0, Math.min(positionMs, totalDuration));
    positionRef.current = clamped;
    setPlayheadPosition(clamped);
    
    if (clamped >= totalDuration) {
      setIsPlaying(false);
    }
  }, [totalDuration]);

  // Frame stepping (24fps)
  const stepForward = useCallback(() => {
    seek(positionRef.current + (1000 / 24));
  }, [seek]);

  const stepBackward = useCallback(() => {
    seek(positionRef.current - (1000 / 24));
  }, [seek]);

  const goToStart = useCallback(() => seek(0), [seek]);
  const goToEnd = useCallback(() => seek(totalDuration), [seek, totalDuration]);
  const skipForward = useCallback((amt = 1000) => seek(positionRef.current + amt), [seek]);
  const skipBackward = useCallback((amt = 1000) => seek(positionRef.current - amt), [seek]);

  return {
    isPlaying,
    playheadPosition,
    getPosition,
    play,
    pause,
    togglePlayPause,
    seek,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
    skipForward,
    skipBackward,
    getSegmentAtPosition,
    getSegmentIndexAtPosition
  };
}

export default useSegmentPlayback;
